import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  subscribeWorkspace,
  createDesk,
  createDrawer,
  createNotebook,
  createText,
  createArticle,
  updateText,
} from "../../services/workspaceService";
import { BACKGROUNDS } from "../../config/backgrounds";
import { useEditLock } from "../../context/EditLockContext";
import { Save, X, Layout as LayoutIcon, Image as ImageIcon, Mic, MicOff } from "lucide-react";
import { useVoiceToText } from "../../hooks/useVoiceToText";
import VoiceFrequencyBars from "../common/VoiceFrequencyBars";
import DictationLangToggle from "../common/DictationLangToggle";
import { appendDictationChunk, tentativeDisplay } from "../../utils/dictationUtils";
import ArticleCanvas from "../common/ArticleCanvas";
import UnlockSeal from "../common/UnlockSeal";
import { extractImageFilesFromFileList, IMAGE_ACCEPT } from "../../utils/inlineImages";
import WorkspacePathPicker from "../workspace/WorkspacePathPicker";
import HomeShell from "../shell/HomeShell";
import deskGlass from "../../styles/deskGlass";
import { useDraftAutosave, clearDraftStorage, readDraftStorage } from "../../hooks/useDraftAutosave";
import { PenLine } from "lucide-react";
import { runFeedbackFlow } from "../../utils/runFeedbackFlow";
import { confirmInApp } from "../common/AppDialog";
import { rememberWritingText } from "../../utils/writingSession";

const glass = deskGlass;

function extractPlainTextFromHtml(html) {
  // Simple, safe-ish plain text extraction for prompts (strip tags, collapse whitespace).
  return String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export default function Editor() {
  const navigate = useNavigate();
  const location = useLocation();
  const { canEdit } = useEditLock();
  const locked = !canEdit;
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);

  // Selections
  const [deskId, setDeskId] = useState("");
  const [drawerId, setDrawerId] = useState("");
  const [notebookId, setNotebookId] = useState("");

  // New names
  const [newDeskName, setNewDeskName] = useState("");
  const [newDrawerName, setNewDrawerName] = useState("");
  const [newNotebookName, setNewNotebookName] = useState("");

  // Content
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveStatus, setSaveStatus] = useState("");
  const [draftId, setDraftId] = useState("");
  const [tentativeText, setTentativeText] = useState("");
  const [dictationLang, setDictationLang] = useState("es");
  const [commitFlash, setCommitFlash] = useState(false);
  
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const draftIdRef = useRef("");
  const marginModeRef = useRef(false);
  const marginInsertRangeRef = useRef(null);
  const flushDraftRef = useRef(() => {});
  const editingTextIdRef = useRef("");
  const editingPathRef = useRef(null);
  const loadedBaselineRef = useRef({ title: "", content: "", textId: "" });

  const [marginMode, setMarginMode] = useState(false);
  const [marginNoteContent, setMarginNoteContent] = useState("");
  const [marginTentativeText, setMarginTentativeText] = useState("");

  const { flushDraftSave } = useDraftAutosave({
    draftId,
    locked,
    title,
    content,
    deskId,
    drawerId,
    notebookId,
    onStatus: setSaveStatus,
  });
  flushDraftRef.current = flushDraftSave;

  useEffect(() => { marginModeRef.current = marginMode; }, [marginMode]);

  const { isRecording, status, startRecording, stopRecording, error: voiceError, voiceLevel, isSilent } = useVoiceToText(
    (finalText) => {
      setCommitFlash(true);
      setTimeout(() => setCommitFlash(false), 350);
      if (marginModeRef.current) {
        setMarginNoteContent((prev) => appendDictationChunk(prev, finalText, finalText?.pauseMs ?? null));
        setMarginTentativeText("");
        setTentativeText("");
      } else {
        setContent((prev) => appendDictationChunk(prev, finalText, finalText?.pauseMs ?? null));
        setTentativeText("");
      }
      flushDraftRef.current();
    },
    (interim) => {
      if (marginModeRef.current) setMarginTentativeText(tentativeDisplay(interim));
      else setTentativeText(tentativeDisplay(interim));
    },
    { language: dictationLang }
  );

  useEffect(() => {
    const unsub = subscribeWorkspace((data) => {
      setWorkspace(data);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Hydrate from navigation state (edit flow or mic auto-start).
  // Runs on EVERY load — including direct opens/reloads with no navigation
  // state — so the local-draft vault always has a session id. Without this,
  // the save flow's local-draft gate blocks all saves on mobile reloads.
  useEffect(() => {
    if (loading) return;

    const st = location.state || {};
    const hasState = Object.keys(st).length > 0;
    const {
      autoStart,
      deskId: sDesk,
      drawerId: sDrawer,
      notebookId: sNotebook,
      textId: sTextId,
      initialTitle,
      initialContent,
    } = st;

    // Stable draft ID: existing text uses textId; new text uses uuid.
    let nextDraftId = sTextId ? String(sTextId) : "";
    if (!nextDraftId) {
      try {
        nextDraftId = localStorage.getItem("voiceEditorActiveDraftId") || "";
      } catch (_e) {}
    }
    if (!nextDraftId) {
      nextDraftId = (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `new-${Date.now()}`);
    }
    try {
      localStorage.setItem("voiceEditorActiveDraftId", nextDraftId);
    } catch (_e) {}

    draftIdRef.current = nextDraftId;
    setDraftId(nextDraftId);
    loadedBaselineRef.current = {
      title: initialTitle || "",
      content: initialContent || "",
      textId: sTextId ? String(sTextId) : "",
    };


    if (hasState) {
      if (sDesk) setDeskId(sDesk);
      if (sDrawer) setDrawerId(sDrawer);
      if (sNotebook) setNotebookId(sNotebook);
      if (initialTitle) setTitle(initialTitle);
      if (initialContent) setContent(initialContent);
    }

    // Restore local draft if it is newer than the initial content.
    // Runs on every load path (with or without navigation state).
    try {
      const raw = readDraftStorage(nextDraftId);
      if (raw) {
        const draft = JSON.parse(raw);
        // If editing existing text, restore only if local draft is newer than the cloud page.
        // If editing a new note, there is no cloud timestamp yet -> restore if draft exists.
        let referenceTs = 0;
        const cloudUpdatedAt =
          workspace?.desks?.[sDesk]?.drawers?.[sDrawer]?.notebooks?.[sNotebook]?.texts?.[sTextId]?.updatedAt;
        if (typeof cloudUpdatedAt === "number") referenceTs = cloudUpdatedAt;

        if (draft?.timestamp && draft.timestamp > referenceTs && (draft.title || draft.content)) {
          if (typeof draft.title === "string") setTitle(draft.title);
          if (typeof draft.content === "string") setContent(draft.content);
          if (typeof draft.deskId === "string" && draft.deskId) setDeskId(draft.deskId);
          if (typeof draft.drawerId === "string" && draft.drawerId) setDrawerId(draft.drawerId);
          if (typeof draft.notebookId === "string" && draft.notebookId) setNotebookId(draft.notebookId);
          setSaveStatus("Borrador local restaurado");
          setTimeout(() => setSaveStatus(""), 1200);
        }
      }
    } catch (e) {
      console.error("[EDITOR] draft restore failed", { name: e?.name, message: e?.message, draftId: nextDraftId });
    }

    // Empty-state rerun (after the replace below): draft id + restore only —
    // never reset editing refs, or updating an existing note would duplicate it.
    if (!hasState) return;

    if (autoStart && !locked) {
      startRecording();
    }

    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, location.state]);

  useEffect(() => {
    const openWritingText = async (event) => {
      const text = event?.detail;
      const nextId = String(text?.id || text?.textId || "");
      if (!nextId || nextId === editingTextIdRef.current) return;

      const baseline = loadedBaselineRef.current;
      const hasUnsaved =
        title !== String(baseline.title || "") ||
        content !== String(baseline.content || "");

      if (hasUnsaved && !locked) {
        const draftSaved = flushDraftRef.current?.();
        if (!draftSaved) {
          setSaveError("No se pudo proteger el borrador local. No cambié de archivo.");
          return;
        }
        const proceed = await confirmInApp({
          title: "Cambiar de archivo",
          message: "Hay cambios sin guardar en la nube. El borrador local ya quedó protegido.",
          confirmLabel: "Cambiar",
          danger: true,
        });
        if (!proceed) return;
      }

      if (isRecording) stopRecording();

      const cloudTitle = String(text.title || "");
      const cloudContent = String(text.content || "");
      let nextTitle = cloudTitle;
      let nextContent = cloudContent;
      let restoredDraft = false;

      try {
        const raw = localStorage.getItem(`voiceEditorDraft:${nextId}`);
        if (raw) {
          const localDraft = JSON.parse(raw);
          const cloudTs = typeof text.updatedAt === "number" ? text.updatedAt : 0;
          if (localDraft?.timestamp && localDraft.timestamp > cloudTs) {
            if (typeof localDraft.title === "string") nextTitle = localDraft.title;
            if (typeof localDraft.content === "string") nextContent = localDraft.content;
            restoredDraft = true;
          }
        }
      } catch (error) {
        console.warn("[EDITOR] target draft restore failed:", error);
      }

      const nextDeskId = String(text.deskId || "");
      const nextDrawerId = String(text.drawerId || "");
      const nextNotebookId = String(text.notebookId || "");

      editingTextIdRef.current = nextId;
      editingPathRef.current = {
        deskId: nextDeskId,
        drawerId: nextDrawerId,
        notebookId: nextNotebookId,
      };
      loadedBaselineRef.current = { title: cloudTitle, content: cloudContent, textId: nextId };
      draftIdRef.current = nextId;
      setDraftId(nextId);
      try { localStorage.setItem("voiceEditorActiveDraftId", nextId); } catch (_e) {}

      setDeskId(nextDeskId);
      setDrawerId(nextDrawerId);
      setNotebookId(nextNotebookId);
      setTitle(nextTitle);
      setContent(nextContent);
      setTentativeText("");
      setMarginMode(false);
      marginModeRef.current = false;
      setMarginNoteContent("");
      setMarginTentativeText("");
      setSaveError("");
      setSaveStatus(restoredDraft ? "Borrador local restaurado" : `Abierto · ${cloudTitle || "Sin título"}`);
      rememberWritingText(text);
    };

    window.addEventListener("trufapp:open-writing-text", openWritingText);
    return () => window.removeEventListener("trufapp:open-writing-text", openWritingText);
  }, [content, isRecording, locked, stopRecording, title]);

  const handleVoiceToggle = () => {
    if (locked) return;
    if (status === "error") {
      console.log("[EDITOR] Voice error state, restarting...");
      stopRecording();
      setTimeout(startRecording, 100);
    } else if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const handleToggleMargin = () => {
    if (locked) return;
    if (!marginModeRef.current) {
      // Activar margen: guardamos el caret para insertar al registrar.
      marginModeRef.current = true;
      setMarginMode(true);
      setTentativeText("");
      setMarginTentativeText("");
      try {
        const sel = window.getSelection();
        if (sel && sel.rangeCount) {
          marginInsertRangeRef.current = sel.getRangeAt(0).cloneRange();
        }
      } catch (_e) {
        marginInsertRangeRef.current = null;
      }
    } else {
      marginModeRef.current = false;
      setMarginMode(false);
      setMarginTentativeText("");
    }
  };

  const handleSave = async () => {
    if (locked) return;
    if (!title.trim() && !content.trim() && !marginNoteContent.trim()) return;
    setSaveError("");
    setSaveStatus("");
    
    // Detener dictado mientras registramos para no mezclar contenido.
    if (isRecording) stopRecording();

    let finalTitle = title.trim();
    if (!finalTitle) {
       // Si hay margen, el título puede salir del contenido principal (sin meter nota al margen).
       const plainContent = content.replace(/<[^>]*>?/gm, '').trim();
       const firstLine = plainContent.split('\n')[0];
       finalTitle = firstLine.length > 30 ? firstLine.substring(0, 27) + "..." : firstLine || "Nota " + new Date().toLocaleTimeString();
    }

    // If margin note exists, insert it visually into the editor HTML and use that final HTML for saving.
    let contentToSave = content.trim();
    if (marginNoteContent.trim()) {
      const asideHtml = (() => {
        const escapeHtml = (str) =>
          String(str)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");

        const safe = escapeHtml(marginNoteContent).replace(/\n/g, "<br/>");
        return `<aside data-kind="margin-note">${safe}</aside>`;
      })();

      let inserted = false;
      try {
        const canvasEl = canvasRef.current;
        const range = marginInsertRangeRef.current;
        if (canvasEl && range && range.startContainer && canvasEl.contains(range.startContainer)) {
          range.deleteContents();
          const asideEl = document.createElement("div");
          asideEl.innerHTML = asideHtml;
          const node = asideEl.firstChild;
          if (node) {
            range.insertNode(node);
            inserted = true;
          }
        }
      } catch (_e) {}

      if (!inserted) {
        contentToSave = (contentToSave || "") + asideHtml;
      } else {
        contentToSave = (canvasRef.current?.innerHTML || "").trim();
      }

      // Sync React state with the final HTML.
      setContent(contentToSave);

      // Clear margin panel so next dictation goes to the main body.
      setMarginMode(false);
      marginModeRef.current = false;
      setMarginNoteContent("");
      setMarginTentativeText("");
    }

    setSaving(true);
    try {
      await runFeedbackFlow({
        actionId: "editor-save",
        label: "Guardar",
        localStep: async () => {
          const ok = flushDraftRef.current?.();
          if (!ok) throw new Error("Borrador local falló");
        },
        dbStep: async () => {
          if (!deskId) {
            await createArticle(finalTitle, contentToSave);
            clearDraftStorage(draftId);
            draftIdRef.current = "";
            setDraftId("");
            setSaveStatus("Guardado en nube");
            setShowSuccess(true);
            setTimeout(() => navigate("/"), 1200);
            return;
          }

          const existingId = editingTextIdRef.current;
          const existingPath = editingPathRef.current;

          if (existingId && existingPath?.deskId && existingPath.drawerId && existingPath.notebookId) {
            await updateText(
              existingPath.deskId,
              existingPath.drawerId,
              existingPath.notebookId,
              existingId,
              { title: finalTitle, content: contentToSave },
            );
            loadedBaselineRef.current = {
              title: finalTitle,
              content: contentToSave,
              textId: existingId,
            };
            clearDraftStorage(draftId);
            draftIdRef.current = existingId;
            setDraftId(existingId);
            try { localStorage.setItem("voiceEditorActiveDraftId", existingId); } catch (_e) {}
            rememberWritingText({
              id: existingId,
              title: finalTitle,
              content: contentToSave,
              ...existingPath,
            });
            setSaveStatus("Guardado en nube ✓");
            setShowSuccess(false);
            return;
          } else {
            let targetDeskId = deskId;
            let targetDrawerId = drawerId;
            let targetNotebookId = notebookId;

            if (targetDeskId === "__new__") {
              targetDeskId = await createDesk(newDeskName.trim() || "Nuevo Escritorio");
              targetDrawerId = "__new__";
            }

            if (!targetDrawerId || targetDrawerId === "__new__") {
              const dName = newDrawerName.trim() || "General";
              targetDrawerId = await createDrawer(targetDeskId, dName);
              targetNotebookId = "__new__";
            }

            if (!targetNotebookId || targetNotebookId === "__new__") {
              const nName = newNotebookName.trim() || "Posts";
              targetNotebookId = await createNotebook(targetDeskId, targetDrawerId, nName, "#8B4513");
            }

            await createText(targetDeskId, targetDrawerId, targetNotebookId, finalTitle, contentToSave);
          }

          clearDraftStorage(draftId);
          draftIdRef.current = "";
          editingTextIdRef.current = "";
          editingPathRef.current = null;
          setDraftId("");
          setSaveStatus("Guardado en nube");
          setShowSuccess(true);
          setTimeout(() => navigate("/drawers"), 1200);
        },
        successMessage: "DB OK",
        errorMessage: "DB / flujo falló",
      });
    } catch (err) {
      console.error("[EDITOR] save flow failed", {
        name: err?.name,
        message: err?.message,
        draftId: draftIdRef.current || draftId,
        hasTitle: Boolean(finalTitle),
        contentLength: contentToSave.length,
        deskId: deskId || null,
      }, err);
      setSaveError("No se pudo guardar. Tu borrador sigue en este dispositivo.");
      setSaveStatus("Error de nube: borrador local protegido");
    } finally {
      setSaving(false);
    }
  };

  const contentToCopySource = () => content;

  const handleCopyText = async () => {
    if (locked) return;
    try {
      const plain = extractPlainTextFromHtml(contentToCopySource());
      const t = (title || "").trim();
      const out = t ? `${t}\n\n${plain}` : plain;
      await navigator.clipboard.writeText(out);
    } catch (e) {
      console.warn("[Editor] clipboard.writeText failed:", e);
      setSaveError("No se pudo copiar al portapapeles.");
      setTimeout(() => setSaveError(""), 2000);
    }
  };

  const handlePickImages = () => {
    if (locked) return;
    canvasRef.current?.focus();
    fileInputRef.current?.click();
  };

  const handleImageFiles = async (e) => {
    if (locked) return;
    const files = extractImageFilesFromFileList(e.target.files);
    e.target.value = "";
    if (!files.length) return;
    await canvasRef.current?.insertFiles(files);
    setTentativeText("");
  };

  if (loading) return null;

  const editorPanel = (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {showSuccess && (
        <div style={panelStyles.successOverlay}>
          <div style={panelStyles.successCard}>
            <div style={panelStyles.successIcon}>✓</div>
            <h2 style={{ color: "#fff", margin: "10px 0 5px" }}>¡Registrado!</h2>
          </div>
        </div>
      )}

      {!locked && (
      <div style={panelStyles.topBar}>
        <div style={{ flex: 1 }}>
          <input
            placeholder="Sin título..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={glass.titleInput}
          />
        </div>
        <div style={{ display: "flex", gap: "8px" }} className="desktop-only">
          <button
            style={panelStyles.actionBtn}
            onClick={handleCopyText}
            title="Copiar texto"
          >
            Copiar
          </button>
          <button style={panelStyles.actionBtn} onClick={handleSave} disabled={saving}>
            <Save size={20} />
          </button>
          <button style={panelStyles.actionBtn} onClick={() => navigate(-1)}>
            <X size={20} />
          </button>
        </div>
      </div>
      )}

      <div style={{ ...glass.glassCanvas, margin: "0", flex: 1, display: "flex", flexDirection: "column", minHeight: 0, borderRadius: 0 }}>
        {locked ? (
          <UnlockSeal size={112} />
        ) : (
        <>
        <ArticleCanvas
          ref={canvasRef}
          html={content}
          tentative={tentativeText}
          commitFlash={commitFlash}
          readOnly={false}
          placeholder="Escribí aquí..."
          onChange={(html) => {
            setContent(html);
            setTentativeText("");
          }}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept={IMAGE_ACCEPT}
          multiple
          style={{ display: "none" }}
          onChange={handleImageFiles}
        />
        {voiceError && (
          <div style={panelStyles.errorBanner}>
            <span>🎙️ {voiceError}</span>
            <button type="button" onClick={handleVoiceToggle} style={{ marginLeft: 10, color: "#fff", background: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.35)", borderRadius: 6, padding: "4px 8px", cursor: "pointer" }}>
              Reintentar
            </button>
          </div>
        )}
        {saveError && (
          <div style={{ ...panelStyles.errorBanner, top: voiceError ? 36 : 0, background: "rgba(231, 76, 60, 0.95)", fontSize: "0.9rem" }}>
            {saveError}
          </div>
        )}
        {marginMode && (
          <div style={panelStyles.marginPanel}>
            <div style={panelStyles.marginPanelTitle}>↳ margen</div>
            <div style={panelStyles.marginPanelBody}>
              {marginNoteContent ? (
                <div style={{ whiteSpace: "pre-wrap" }}>{marginNoteContent}</div>
              ) : (
                <div style={{ opacity: 0.7 }}>Dictando al margen...</div>
              )}
              {marginTentativeText && (
                <div style={panelStyles.marginTentative}>
                  {typeof marginTentativeText === "string"
                    ? marginTentativeText
                    : marginTentativeText?.transcript || ""}
                </div>
              )}
            </div>
            <div style={panelStyles.marginPanelHint}>Se inserta al registrar.</div>
          </div>
        )}
        </>
        )}
      </div>

      {!locked && (
      <div style={glass.glassBottomBar}>
        <div style={glass.glassToolbar}>
          <DictationLangToggle
            value={dictationLang}
            onChange={(lang) => {
              if (locked) return;
              setDictationLang(lang);
              if (isRecording) {
                stopRecording();
                setTimeout(startRecording, 120);
              }
            }}
          />
          <VoiceFrequencyBars active={isRecording} level={voiceLevel} />
          {saveStatus && (
            <div style={{ fontSize: "0.7rem", color: "#D4AF37", maxWidth: "160px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={saveStatus}>
              {saveStatus}
            </div>
          )}
          {isRecording && isSilent && (
            <div style={{ fontSize: "0.7rem", color: "#D4AF37", maxWidth: "200px", lineHeight: 1.15 }}>
              <div>Silencio OK · Sigo escuchando</div>
            </div>
          )}
          <button
            onClick={handleVoiceToggle}
            style={{
              ...panelStyles.toolBtn,
              color: status === "error" ? "#ff4d4d" : status === "connecting" ? "#D4AF37" : isSilent ? "#D4AF37" : isRecording ? "#4ade80" : "#fff",
              background: status === "error" ? "rgba(255,77,77,0.15)" : isSilent ? "rgba(212,175,55,0.16)" : isRecording ? "rgba(74,222,128,0.15)" : "rgba(255,255,255,0.05)",
              borderColor: isSilent ? "#D4AF37" : status === "ready" ? "#4ade80" : "transparent",
              borderWidth: "1px",
              borderStyle: "solid",
              ...(locked ? { opacity: 0.4, cursor: "not-allowed", filter: "grayscale(1)" } : {}),
            }}
            disabled={locked}
          >
            {isRecording ? <MicOff size={24} /> : <Mic size={24} />}
          </button>
          <button onClick={handlePickImages} style={locked ? { ...panelStyles.toolBtn, opacity: 0.4, cursor: "not-allowed" } : panelStyles.toolBtn} disabled={locked}>
            <ImageIcon size={22} />
          </button>
          <button
            onClick={handleToggleMargin}
            style={{
              ...panelStyles.toolBtn,
              borderColor: marginMode ? "rgba(212,175,55,0.45)" : "transparent",
              background: marginMode ? "rgba(212,175,55,0.12)" : "transparent",
              ...(locked ? { opacity: 0.4, cursor: "not-allowed" } : {}),
            }}
            disabled={locked}
          >
            <span style={{ fontSize: "0.8rem", fontWeight: 700, color: "#D4AF37" }}>{marginMode ? "Volver" : "↳ margen"}</span>
          </button>
          <div style={panelStyles.separator} />
          <div style={{ ...panelStyles.selectorWrapper, ...(locked ? { pointerEvents: "none", opacity: 0.45 } : {}) }}>
            <LayoutIcon size={16} color="rgba(255,255,255,0.4)" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <WorkspacePathPicker
                workspace={workspace}
                deskId={deskId}
                drawerId={drawerId}
                notebookId={notebookId}
                newNotebookName={newNotebookName}
                onChangePath={({ deskId: nextDeskId, drawerId: nextDrawerId, notebookId: nextNotebookId }) => {
                  setDeskId(nextDeskId);
                  setDrawerId(nextDrawerId);
                  setNotebookId(nextNotebookId);
                }}
                onQuickCreateNotebook={(name) => {
                  setNewNotebookName(name);
                  setNotebookId("__new__");
                }}
              />
            </div>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", gap: "8px" }}>
            <button
              style={{ ...panelStyles.actionBtn, width: "auto", padding: "0 16px", gap: "8px", background: "#D4AF37", color: "#1A100C", ...(locked ? { opacity: 0.4, cursor: "not-allowed" } : {}) }}
              onClick={handleSave}
              disabled={saving || locked}
            >
              <Save size={20} />
              <span className="toolbar-text">REGISTRAR</span>
            </button>
            <button style={{ ...panelStyles.actionBtn, background: "rgba(255,255,255,0.05)" }} onClick={() => navigate("/dashboard")}>
              <X size={20} />
            </button>
          </div>
        </div>
      </div>
      )}
    </div>
  );

  return (
    <HomeShell background={BACKGROUNDS.dashboard} style={{ height: "100%", minHeight: 0 }}>
      <div style={{ ...glass.container, height: "100%", minHeight: 0 }}>
        {editorPanel}
      </div>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
        @media (max-width: 768px) {
          .desktop-only { display: none !important; }
          .toolbar-text { display: none; }
        }
      `}</style>
    </HomeShell>
  );
}

const panelStyles = {
  topBar: {
    height: "64px",
    display: "flex",
    alignItems: "center",
    padding: "0 16px",
    background: "rgba(20, 20, 20, 0.4)",
    backdropFilter: "blur(24px)",
    borderBottom: "1px solid rgba(255,255,255,0.05)",
    gap: "12px",
    flexShrink: 0,
  },
  actionBtn: {
    height: "44px",
    minWidth: "44px",
    borderRadius: "12px",
    border: "1px solid rgba(255,255,255,0.1)",
    background: "rgba(255,255,255,0.08)",
    color: "#fff",
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer",
    fontSize: "0.75rem",
    fontWeight: "bold",
    letterSpacing: "1px",
  },
  bottomBar: {
    height: "80px",
    background: "rgba(20, 20, 20, 0.6)",
    backdropFilter: "blur(24px)",
    borderTop: "1px solid rgba(255, 255, 255, 0.1)",
    flexShrink: 0,
    paddingBottom: "env(safe-area-inset-bottom)",
  },
  glassToolbar: {
    height: "100%",
    display: "flex",
    alignItems: "center",
    padding: "0 16px",
    gap: "12px",
  },
  toolBtn: {
    background: "transparent",
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    padding: "10px",
    borderRadius: "12px",
    border: "1px solid transparent",
  },
  separator: { width: "1px", height: "24px", background: "rgba(255,255,255,0.1)" },
  selectorWrapper: { display: "flex", alignItems: "center", gap: "8px", opacity: 0.6 },
  ghostSelect: {
    background: "transparent", border: "none", color: "#fff", fontSize: "0.85rem", outline: "none"
  },
  canvas: {
    flex: 1,
    position: "relative",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  errorBanner: {
    position: "absolute", top: 0, left: 0, right: 0,
    background: "rgba(231, 76, 60, 0.9)",
    color: "#fff", padding: "8px", textAlign: "center", fontSize: "0.85rem",
  },
  successOverlay: {
    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
    background: "rgba(0,0,0,0.85)", backdropFilter: "blur(16px)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 2000,
  },
  successCard: {
    textAlign: "center", animation: "fadeIn 0.4s cubic-bezier(0.23, 1, 0.32, 1)",
  },
  successIcon: {
    width: "80px", height: "80px", borderRadius: "40px",
    background: "rgba(74, 222, 128, 0.2)",
    color: "#4ade80", fontSize: "40px",
    display: "flex", alignItems: "center", justifyContent: "center",
    margin: "0 auto 20px", border: "2px solid #4ade80",
  },
  marginPanel: {
    position: "absolute",
    right: 16,
    bottom: 96,
    width: "min(360px, calc(100% - 32px))",
    maxHeight: 200,
    overflow: "hidden",
    borderRadius: 14,
    padding: "12px 12px",
    background: "rgba(20,20,20,0.92)",
    border: "1px solid rgba(212,175,55,0.22)",
    backdropFilter: "blur(18px)",
    zIndex: 1200,
  },
  marginPanelTitle: {
    color: "#D4AF37",
    fontWeight: 800,
    fontSize: "0.85rem",
    marginBottom: 8,
  },
  marginPanelBody: {
    color: "#eee",
    fontSize: "0.9rem",
    lineHeight: 1.25,
    maxHeight: 120,
    overflow: "auto",
  },
  marginTentative: {
    marginTop: 6,
    color: "#D4AF37",
    fontStyle: "italic",
    whiteSpace: "pre-wrap",
  },
  marginPanelHint: {
    marginTop: 8,
    color: "rgba(255,255,255,0.55)",
    fontSize: "0.75rem",
  }
};
